<template>
	<Input
		:id="componentId"
		ref="inputRef"
		v-model="formattedValue"
		variant="insideLabel"
		type="text"
		inputmode="decimal"
		:placeholder="placeholder"
		:error="error"
		:class="[
			cn('w-full text-sm text-primary shadow-none', props.class),
			{ 'pl-7': unitPosition === 'left' }
		]"
		:label-class="
			cn(
				'font-normal text-sm peer-focus:top-1 peer-placeholder-shown:top-[15px] peer-placeholder-shown:opacity-100 text-primary-light-1!',
				props.labelClass
			)
		"
		@update:is-focused="handleInputFocus"
	>
		<template #affix>
			<UnitDisplay
				:affix="unit"
				:affix-position="unitPosition"
				:is-shown="isAffixShown"
				:class="props.affixClass"
			/>
		</template>
	</Input>
</template>

<script setup lang="ts">
import type { InputProps } from '../ui/input';
import type { CurrencyInputOptions } from 'vue-currency-input';

import { cn } from '@/utils';
import { useId } from 'vue';
import { CurrencyDisplay, useCurrencyInput } from 'vue-currency-input';

import { Input } from '../ui/input';

type CurrencyInputProps = Omit<InputProps, 'variant' | 'modelValue'> & {
	unitPosition: 'left' | 'right';
	unit: string;
	currencyOptions?: CurrencyInputOptions;
	affixClass?: string;
};

defineEmits<{
	(e: 'onChange', value: number | null): void;
	(e: 'change', value: number | null): void;
}>();

const props = withDefaults(defineProps<CurrencyInputProps>(), {
	affixClass: undefined,
	currencyOptions: () => ({
		locale: 'pt-BR',
		currency: 'BRL',
		currencyDisplay: CurrencyDisplay.hidden,
		hideCurrencySymbolOnFocus: true,
		hideGroupingSeparatorOnFocus: false,
		hideNegligibleDecimalDigitsOnFocus: false,
		autoDecimalDigits: false,
		useGrouping: true,
		accountingSign: false,
		precision: 0
	})
});

const componentId = useId();

const modelValue = defineModel<number | null>();

const { inputRef, formattedValue, setValue } = useCurrencyInput(
	props.currencyOptions
);

const isInputFocused = ref(false);

function handleInputFocus(value: boolean) {
	isInputFocused.value = value;
}

function shouldShowAffix(focus: boolean, value?: string | number | null) {
	if (focus) return true;

	if (value && Number(value) >= 0) return true;

	return !!value?.toString()?.length;
}

const isAffixShown = computed(() =>
	shouldShowAffix(isInputFocused.value, modelValue.value)
);

watch(
	modelValue,
	() => {
		setValue(modelValue.value || null);
	},
	{ immediate: true }
);
</script>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style scoped></style>
